import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import * as Yup from "yup"

import CustomSelect from "../../../components/CustomSelect"
import ModalComponent from "../../../components/Modal"
import SweetAlerts from "../../../components/SweetAlerts"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"
import { vehicleType } from "../../../lib/GlobalVariables"

const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
]

const verificationStatusOptions = [
  { value: "VERIFIED", label: "VERIFIED" },
  { value: "NOT_VERIFIED", label: "NOT VERIFIED" },
]

const AddEditFleetVehicle = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [fleetVendorData, setFleetVendorData] = useState([])
  const [brandData, setBrandData] = useState([])
  const [connectorData, setConnectorData] = useState([])
  const [showReleaseAlert, setShowReleaseAlert] = useState(false)
  const [pendingAssignment, setPendingAssignment] = useState(null)

  const validationSchema = Yup.object({
    fleet_vendor_id: isUpdate
      ? Yup.mixed().nullable()
      : Yup.mixed()
          .nullable()
          .test("required", "Fleet vendor is required", (value) => {
            return Boolean(value?.value ?? value)
          }),
    registration_number: Yup.string()
      .trim()
      .min(2, "Enter at least 2 characters for registration number")
      .matches(
        /^[A-Z0-9]+$/,
        "Registration number must contain only capital letters and numbers",
      )
      .required("Registration number is required"),
    brand_id: Yup.mixed()
      .test("required", "Brand is required", (value) => {
        const normalizedValue = value?.value ?? value
        return !!normalizedValue || normalizedValue === 0
      })
      .required("Brand is required"),
    model: Yup.string()
      .trim()
      .min(2, "Enter at least 2 characters for model")
      .required("Model is required"),
    manufacturing_year: Yup.mixed()
      .test("required", "Manufacturing year is required", (value) => {
        const normalizedValue = value?.value ?? value
        return !!normalizedValue || normalizedValue === 0
      })
      .required("Manufacturing year is required"),
    vehicle_type: Yup.mixed()
      .test("required", "Vehicle type is required", (value) => {
        const normalizedValue = value?.value ?? value
        return !!normalizedValue || normalizedValue === 0
      })
      .required("Vehicle type is required"),
    battery_capacity: Yup.number()
      .typeError("Battery capacity is required")
      .min(1, "Battery capacity must be greater than 0")
      .required("Battery capacity is required"),
    battery_voltage: Yup.number()
      .typeError("Battery voltage is required")
      .min(1, "Battery voltage must be greater than 0")
      .required("Battery voltage is required"),
    connector_ids: Yup.array()
      .min(1, "Please provide at least one connector")
      .required("At least one connector is required"),
    ...(isUpdate
      ? {
          status: Yup.string().required("Status is required"),
          verification_status: Yup.string().required(
            "Verification status is required",
          ),
        }
      : {}),
  })

  const fetchFleetVendorData = async (currentVehicle) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.fleetVendorList}?page=1&limit=200&status=ACTIVE`,
      })

      if (response?.data?.code === 200) {
        const vendors = (response?.data?.data || [])
          .filter(
            (vendor) =>
              vendor?.status === "ACTIVE" &&
              vendor?.primary_fleet_client?.status === "ACTIVE" &&
              !vendor?.primary_fleet_client?.deletedAt,
          )
          .map((vendor) => ({
            value: vendor?.id,
            label: vendor?.name,
            primaryFleetClientId: vendor?.primary_fleet_client_id,
            primaryFleetClientName: vendor?.primary_fleet_client?.name,
          }))
          .filter(
            (vendor) =>
              vendor.value &&
              vendor.label &&
              vendor.primaryFleetClientId &&
              vendor.primaryFleetClientName,
          )

        const currentVendor = currentVehicle?.fleet_vendor
          ? {
              value: currentVehicle.fleet_vendor.id,
              label: currentVehicle.fleet_vendor.name,
              primaryFleetClientId:
                currentVehicle.fleet_vendor.primary_fleet_client_id,
              primaryFleetClientName:
                currentVehicle.fleet_clients?.[0]?.name || "-",
            }
          : null

        if (
          currentVendor &&
          !vendors.some((vendor) => vendor.value === currentVendor.value)
        ) {
          vendors.unshift(currentVendor)
        }

        setFleetVendorData(vendors)
      }
    } catch (error) {
      toast.error(error?.message || "Unable to fetch fleet vendors")
    }
  }

  const fetchBrandData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.brandList,
      })

      if (response?.data?.code === 200) {
        const brands = (response?.data?.data || []).map((brand) => ({
          value: brand?.id,
          label: brand?.brand_name,
        }))
        const commercialBrand = brands.find(
          (brand) => brand.label?.trim().toLowerCase() === "commercial",
        )
        setBrandData(commercialBrand ? [commercialBrand] : [])
        if (!commercialBrand) {
          toast.error("Commercial brand is not configured. Fleet vehicle cannot be added.")
        }
        return commercialBrand || null
      }
      return null
    } catch (error) {
      toast.error(error?.message || "Unable to fetch brands")
      return null
    }
  }

  const fetchConnectorData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.connectorList,
      })

      if (response?.data?.code === 200) {
        const connectors = (response?.data?.data || []).map((connector) => ({
          value: connector?.id,
          label: connector?.connector_type,
        }))

        setConnectorData(connectors)
      }
    } catch (error) {
      toast.error(error?.message || "Unable to fetch connectors")
    }
  }

  const saveVehicle = async (values, resetForm) => {
    try {
      setLoading(true)

      const selectedVendor = values?.fleet_vendor_id
      const selectedVendorId = selectedVendor?.value ?? selectedVendor ?? null
      const originalVendorId = isUpdate?.fleet_vendor_id || null

      const payload = {
        ...(!isUpdate || originalVendorId !== selectedVendorId
          ? { fleet_vendor_id: selectedVendorId }
          : {}),
        registration_number: values?.registration_number?.trim(),
        brand_id: Number(values?.brand_id?.value ?? values?.brand_id),
        model: values?.model?.trim(),
        manufacturing_year: Number(
          values?.manufacturing_year?.value ?? values?.manufacturing_year,
        ),
        vehicle_type:
          values?.vehicle_type?.label ??
          values?.vehicle_type?.value ??
          values?.vehicle_type,
        battery_capacity: Number(values?.battery_capacity),
        battery_voltage: Number(values?.battery_voltage),
        connector_ids: (values?.connector_ids || []).map((connector) =>
          Number(connector?.value ?? connector),
        ),
        ...(isUpdate
          ? {
              status: values?.status || "ACTIVE",
              verification_status:
                values?.verification_status || "NOT_VERIFIED",
            }
          : {}),
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateFleetVehicle + isUpdate.id
          : admin.createFleetVehicle,
        data: payload,
      })

      if (response?.data?.code === 200 || response?.data?.code === 201) {
        toast.success(response?.data?.message)

        setShow(false)
        setIsUpdate(false)
        resetForm()
        fetchData()
      } else {
        throw new Error(
          response?.data?.message || "Unable to save Fleet Vehicle",
        )
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (values, { resetForm }) => {
    const originalVendorId = isUpdate?.fleet_vendor_id || null
    const nextVendorId =
      values?.fleet_vendor_id?.value ?? values?.fleet_vendor_id ?? null

    if (isUpdate && originalVendorId !== nextVendorId) {
      setPendingAssignment({ values, resetForm })
      return
    }

    await saveVehicle(values, resetForm)
  }

  const handleAssignmentConfirm = async () => {
    if (!pendingAssignment) return

    const { values: pendingValues, resetForm: pendingResetForm } =
      pendingAssignment
    setPendingAssignment(null)
    await saveVehicle(pendingValues, pendingResetForm)
  }

  const handleRelease = async () => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateFleetVehicle + isUpdate.id,
        data: { verification_status: "NOT_VERIFIED" },
      })

      if (response?.data?.code === 200) {
        toast.success(response?.data?.message)
        setShowReleaseAlert(false)
        setShow(false)
        setIsUpdate(false)
        fetchData()
      } else {
        throw new Error(
          response?.data?.message || "Unable to release Fleet Vehicle",
        )
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          fleet_vendor_id: isUpdate?.fleet_vendor
            ? {
                value: isUpdate.fleet_vendor.id,
                label: isUpdate.fleet_vendor.name,
                primaryFleetClientId:
                  isUpdate.fleet_vendor.primary_fleet_client_id,
                primaryFleetClientName:
                  isUpdate.fleet_clients?.[0]?.name || "-",
              }
            : null,
          registration_number: isUpdate?.registration_number || "",
          brand_id: "",
          model: isUpdate?.model || "",
          manufacturing_year: isUpdate?.manufacturing_year || "",
          vehicle_type:
            vehicleType.find(
              (type) =>
                String(type.value) === String(isUpdate?.vehicle_type) ||
                String(type.label) === String(isUpdate?.vehicle_type),
            ) || "",
          battery_capacity: isUpdate?.battery_capacity || "",
          battery_voltage: isUpdate?.battery_voltage || "",
          connector_ids:
            isUpdate?.connectors?.map((connector) => ({
              value: connector?.id,
              label: connector?.connector_type,
            })) || [],
          status: isUpdate?.status || "ACTIVE",
          verification_status: isUpdate?.verification_status || "NOT_VERIFIED",
        }
      : {
          fleet_vendor_id: null,
          registration_number: "",
          brand_id: "",
          model: "",
          manufacturing_year: "",
          vehicle_type: "",
          battery_capacity: "",
          battery_voltage: "",
          connector_ids: [],
          status: "ACTIVE",
          verification_status: "NOT_VERIFIED",
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { values, errors, touched, setFieldValue, resetForm } = formik
  const commercialBrand = brandData[0] || null

  useEffect(() => {
    if (show) {
      fetchFleetVendorData(isUpdate)
      fetchBrandData().then((brand) => {
        if (brand) setFieldValue("brand_id", brand)
      })
      fetchConnectorData()
    }
  }, [show, isUpdate])

  const currentYear = new Date().getFullYear()
  const manufacturingYears = Array.from({ length: 30 }, (_, index) => {
    const year = currentYear - index

    return {
      value: year,
      label: String(year),
    }
  })

  return (
    <>
      <ModalComponent
        show={show}
        modalsize="lg"
        backdrop="static"
        animationFade={true}
        onHide={() => {
          handleClose()
          resetForm()
        }}
        loading={loading}
        modeltitle={isUpdate ? "Update Fleet Vehicle" : "Add Fleet Vehicle"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext="Cancel"
        handleSubmit={formik.handleSubmit}
        children={
        <Form noValidate>
          <Row xs="auto">
            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Fleet Vendor{" "}
                {!isUpdate && <span className="required_star">*</span>}
              </label>

              <CustomSelect
                value={values?.fleet_vendor_id || null}
                handleSelectChange={(selectedVendor) => {
                  setFieldValue("fleet_vendor_id", selectedVendor || null)
                }}
                options={
                  isUpdate?.status === "INACTIVE" && isUpdate?.fleet_vendor
                    ? fleetVendorData.filter(
                        (vendor) => vendor.value === isUpdate.fleet_vendor_id,
                      )
                    : fleetVendorData
                }
                isDisabled={
                  isUpdate?.status === "INACTIVE" && !isUpdate?.fleet_vendor
                }
                isMulti={false}
                placeholder="Select Fleet Vendor"
              />

              {touched?.fleet_vendor_id && errors?.fleet_vendor_id && (
                <p className="invalid_error">{errors.fleet_vendor_id}</p>
              )}
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="primaryFleetClient"
                label="Primary Fleet Client"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  value={
                    values?.fleet_vendor_id?.primaryFleetClientName ||
                    "Select a fleet vendor to view its fleet client"
                  }
                  readOnly
                  disabled
                />
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="registrationNumber"
                label={
                  <>
                    Registration Number <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder=""
                  name="registration_number"
                  autoComplete="false"
                  maxLength={50}
                  value={values?.registration_number}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "registration_number",
                      e.target.value.toUpperCase(),
                    )
                  }
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={
                    touched?.registration_number && errors?.registration_number
                  }
                  isValid={
                    touched?.registration_number && !errors?.registration_number
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.registration_number}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Brand <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={commercialBrand || null}
                options={commercialBrand ? [commercialBrand] : []}
                isDisabled={!commercialBrand}
                placeholder={commercialBrand ? "" : "Commercial brand unavailable"}
              />

              {touched?.brand_id && errors?.brand_id && (
                <p className="invalid_error">{errors.brand_id}</p>
              )}
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="fleetVehicleModel"
                label={
                  <>
                    Model <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder=""
                  name="model"
                  autoComplete="false"
                  maxLength={100}
                  value={values?.model}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={touched?.model && errors?.model}
                  isValid={touched?.model && !errors?.model}
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.model}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Manufacturing Year <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  manufacturingYears.find(
                    (year) =>
                      Number(year.value) === Number(values?.manufacturing_year),
                  ) || null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue(
                    "manufacturing_year",
                    selectedOption ? selectedOption.value : "",
                  )
                }}
                options={manufacturingYears}
                placeholder="Select Manufacturing Year"
              />

              {touched?.manufacturing_year && errors?.manufacturing_year && (
                <p className="invalid_error">{errors.manufacturing_year}</p>
              )}
            </Col>

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Vehicle Type <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  vehicleType.find(
                    (type) =>
                      String(type.value) ===
                        String(
                          values?.vehicle_type?.value ?? values?.vehicle_type,
                        ) ||
                      String(type.label) ===
                        String(
                          values?.vehicle_type?.label ?? values?.vehicle_type,
                        ),
                  ) || null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue("vehicle_type", selectedOption || "")
                }}
                options={vehicleType}
                placeholder="Select Vehicle Type"
              />

              {touched?.vehicle_type && errors?.vehicle_type && (
                <p className="invalid_error">{errors.vehicle_type}</p>
              )}
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="batteryCapacity"
                label={
                  <>
                    Battery Capacity <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="number"
                  placeholder=""
                  name="battery_capacity"
                  value={values?.battery_capacity}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={
                    touched?.battery_capacity && errors?.battery_capacity
                  }
                  isValid={
                    touched?.battery_capacity && !errors?.battery_capacity
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.battery_capacity}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="batteryVoltage"
                label={
                  <>
                    Battery Voltage <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="number"
                  placeholder=""
                  name="battery_voltage"
                  value={values?.battery_voltage}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={
                    touched?.battery_voltage && errors?.battery_voltage
                  }
                  isValid={touched?.battery_voltage && !errors?.battery_voltage}
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.battery_voltage}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Connectors <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={connectorData.filter((connector) =>
                  (values?.connector_ids || []).some(
                    (selectedConnector) =>
                      Number(selectedConnector?.value ?? selectedConnector) ===
                      Number(connector.value),
                  ),
                )}
                handleSelectChange={(selectedOptions) => {
                  setFieldValue(
                    "connector_ids",
                    selectedOptions
                      ? selectedOptions.map((option) => ({
                          value: option?.value,
                          label: option?.label,
                        }))
                      : [],
                  )
                }}
                options={connectorData}
                isMulti={true}
                placeholder="Select Connectors"
              />

              {touched?.connector_ids && errors?.connector_ids && (
                <p className="invalid_error">{errors.connector_ids}</p>
              )}
            </Col>

            {isUpdate && (
              <>
                <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
                  <label>
                    Status <span className="required_star">*</span>
                  </label>

                  <CustomSelect
                    value={
                      statusOptions.find(
                        (status) => status.value === values?.status,
                      ) || null
                    }
                    handleSelectChange={(selectedOption) => {
                      setFieldValue("status", selectedOption?.value || "ACTIVE")
                    }}
                    options={statusOptions}
                    placeholder="Select Status"
                  />

                  {touched?.status && errors?.status && (
                    <p className="invalid_error">{errors.status}</p>
                  )}
                </Col>

                <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
                  <label>
                    Verification Status <span className="required_star">*</span>
                  </label>

                  <CustomSelect
                    value={
                      verificationStatusOptions.find(
                        (status) =>
                          status.value === values?.verification_status,
                      ) || null
                    }
                    handleSelectChange={(selectedOption) => {
                      if (
                        values?.verification_status === "VERIFIED" &&
                        selectedOption?.value === "NOT_VERIFIED"
                      ) {
                        setShowReleaseAlert(true)
                        return
                      }

                      setFieldValue(
                        "verification_status",
                        selectedOption?.value || "NOT_VERIFIED",
                      )
                    }}
                    options={verificationStatusOptions}
                    placeholder="Select Verification Status"
                  />

                  {touched?.verification_status &&
                    errors?.verification_status && (
                      <p className="invalid_error">
                        {errors.verification_status}
                      </p>
                    )}
                </Col>
              </>
            )}
          </Row>
        </Form>
        }
      />

      <SweetAlerts
        showAlert={showReleaseAlert}
        loading={loading}
        alertMessage="Are you sure you want to release this fleet vehicle? It will become available for another driver to claim."
        handleClose={() => setShowReleaseAlert(false)}
        handleAlert={handleRelease}
      />

      <SweetAlerts
        showAlert={Boolean(pendingAssignment)}
        loading={loading}
        alertMessage={
          pendingAssignment?.values?.fleet_vendor_id
            ? `Change Fleet Vendor from ${
                isUpdate?.fleet_vendor?.name || "Unassigned"
              } to ${pendingAssignment.values.fleet_vendor_id.label}?`
            : "Remove this vehicle's Fleet Vendor assignment?"
        }
        alertSubMessage={
          pendingAssignment?.values?.fleet_vendor_id
            ? `The Fleet Client will change from ${
                isUpdate?.fleet_clients?.[0]?.name || "Unassigned"
              } to ${pendingAssignment.values.fleet_vendor_id.primaryFleetClientName}.`
            : `The Fleet Client ${
                isUpdate?.fleet_clients?.[0]?.name || "relationship"
              } will be retained.`
        }
        handleClose={() => setPendingAssignment(null)}
        handleAlert={handleAssignmentConfirm}
      />
    </>
  )
}

export default AddEditFleetVehicle
